/**
 * Maps layout blocks to guest-facing section hashes for CTA "Section" destinations.
 * Hashes must match the `id` attributes published by core-components blocks.
 */

import type { PageMeta } from '../content/pageMeta';
import { LAYOUT_COMPONENT_TYPE, type LayoutBlock } from '../content/studio';
import { PAGE_SLUG, publicPathForPageSlug } from '../routing/pageRouting';

export interface SiteSectionHashMeta {
  /** Fragment without `#` (e.g. `reserve`). */
  hash: string;
  /** Preferred sort weight (lower = earlier in dropdown). */
  order: number;
  labels: { de: string; en: string; vi: string };
}

/**
 * Component types that publish a scroll target. Hero `#top` / `#about` omitted —
 * they are not useful primary CTA destinations.
 */
export const SITE_SECTION_BY_COMPONENT_TYPE: Partial<
  Record<string, SiteSectionHashMeta>
> = {
  [LAYOUT_COMPONENT_TYPE.reservationFormEmbed]: {
    hash: 'reserve',
    order: 10,
    labels: { de: 'Reservierung', en: 'Reservation', vi: 'Đặt bàn' },
  },
  [LAYOUT_COMPONENT_TYPE.reservationThirdPartyBook]: {
    hash: 'reserve',
    order: 10,
    labels: { de: 'Reservierung', en: 'Reservation', vi: 'Đặt bàn' },
  },
  [LAYOUT_COMPONENT_TYPE.reservationMessengerBook]: {
    hash: 'reserve',
    order: 10,
    labels: { de: 'Reservierung', en: 'Reservation', vi: 'Đặt bàn' },
  },
  [LAYOUT_COMPONENT_TYPE.reservationCallBook]: {
    hash: 'reserve',
    order: 10,
    labels: { de: 'Reservierung', en: 'Reservation', vi: 'Đặt bàn' },
  },
  [LAYOUT_COMPONENT_TYPE.menuGrid3Column]: {
    hash: 'menu',
    order: 20,
    labels: { de: 'Speisekarte', en: 'Menu', vi: 'Thực đơn' },
  },
  [LAYOUT_COMPONENT_TYPE.dynamicMenuFlow]: {
    hash: 'menu',
    order: 20,
    labels: { de: 'Speisekarte', en: 'Menu', vi: 'Thực đơn' },
  },
  [LAYOUT_COMPONENT_TYPE.simpleMenuStrip]: {
    hash: 'menu',
    order: 20,
    labels: { de: 'Speisekarte', en: 'Menu', vi: 'Thực đơn' },
  },
  [LAYOUT_COMPONENT_TYPE.drinksListStatic]: {
    hash: 'drinks',
    order: 25,
    labels: { de: 'Getränke', en: 'Drinks', vi: 'Đồ uống' },
  },
  [LAYOUT_COMPONENT_TYPE.dynamicCocktailSpotlight]: {
    hash: 'cocktail',
    order: 26,
    labels: { de: 'Signature', en: 'Signature cocktail', vi: 'Cocktail signature' },
  },
  [LAYOUT_COMPONENT_TYPE.liveTapListSync]: {
    hash: 'taps',
    order: 27,
    labels: { de: 'Zapfhähne', en: 'On tap', vi: 'Bia vòi' },
  },
  [LAYOUT_COMPONENT_TYPE.vipTableBookingStrip]: {
    hash: 'vip',
    order: 30,
    labels: { de: 'VIP', en: 'VIP', vi: 'VIP' },
  },
  [LAYOUT_COMPONENT_TYPE.privatePartyInquiryForm]: {
    hash: 'party',
    order: 31,
    labels: { de: 'Events', en: 'Private events', vi: 'Sự kiện' },
  },
  [LAYOUT_COMPONENT_TYPE.staticLocationText]: {
    hash: 'location',
    order: 40,
    labels: { de: 'Standort', en: 'Location', vi: 'Địa điểm' },
  },
  [LAYOUT_COMPONENT_TYPE.addressMapBlock]: {
    hash: 'map',
    order: 41,
    labels: { de: 'Karte', en: 'Map', vi: 'Bản đồ' },
  },
  [LAYOUT_COMPONENT_TYPE.simpleOpeningHours]: {
    hash: 'hours',
    order: 42,
    labels: { de: 'Öffnungszeiten', en: 'Hours', vi: 'Giờ mở cửa' },
  },
  [LAYOUT_COMPONENT_TYPE.liveGpsLocationEmbed]: {
    hash: 'gps',
    order: 43,
    labels: { de: 'Live-Standort', en: 'Live location', vi: 'Vị trí trực tiếp' },
  },
  [LAYOUT_COMPONENT_TYPE.weeklyScheduleRouteStrip]: {
    hash: 'schedule',
    order: 44,
    labels: { de: 'Tourplan', en: 'Schedule', vi: 'Lịch trình' },
  },
  [LAYOUT_COMPONENT_TYPE.imageGallery]: {
    hash: 'gallery',
    order: 50,
    labels: { de: 'Galerie', en: 'Gallery', vi: 'Thư viện ảnh' },
  },
  [LAYOUT_COMPONENT_TYPE.kitchenTeamBlock]: {
    hash: 'team',
    order: 54,
    labels: { de: 'Küche & Team', en: 'Kitchen & team', vi: 'Bếp & đội ngũ' },
  },
  [LAYOUT_COMPONENT_TYPE.chefStoryBlock]: {
    hash: 'story',
    order: 55,
    labels: { de: 'Geschichte', en: 'Story', vi: 'Câu chuyện' },
  },
  [LAYOUT_COMPONENT_TYPE.tastingMenuCourseTimeline]: {
    hash: 'tasting',
    order: 56,
    labels: { de: 'Tasting', en: 'Tasting menu', vi: 'Thực đơn tasting' },
  },
  [LAYOUT_COMPONENT_TYPE.allergyCaptureSecureForm]: {
    hash: 'allergies',
    order: 57,
    labels: { de: 'Allergien', en: 'Allergies', vi: 'Dị ứng' },
  },
  [LAYOUT_COMPONENT_TYPE.liveBakeryCounterList]: {
    hash: 'bakery',
    order: 60,
    labels: { de: 'Theke', en: 'Bakery counter', vi: 'Quầy bánh' },
  },
  [LAYOUT_COMPONENT_TYPE.socialFeedGrid]: {
    hash: 'social',
    order: 61,
    labels: { de: 'Social', en: 'Social', vi: 'Mạng xã hội' },
  },
  [LAYOUT_COMPONENT_TYPE.coffeeSubscriptionMatrix]: {
    hash: 'subscribe',
    order: 62,
    labels: { de: 'Abo', en: 'Subscription', vi: 'Đăng ký' },
  },
  [LAYOUT_COMPONENT_TYPE.preOrderPickupForm]: {
    hash: 'preorder',
    order: 63,
    labels: { de: 'Vorbestellung', en: 'Pre-order', vi: 'Đặt trước' },
  },
  [LAYOUT_COMPONENT_TYPE.cateringInquiryForm]: {
    hash: 'catering',
    order: 64,
    labels: { de: 'Catering', en: 'Catering', vi: 'Tiệc đãi' },
  },
  [LAYOUT_COMPONENT_TYPE.faqBlock]: {
    hash: 'faq',
    order: 65,
    labels: { de: 'FAQ', en: 'FAQ', vi: 'FAQ' },
  },
  [LAYOUT_COMPONENT_TYPE.popUpTicketPurchaseCard]: {
    hash: 'tickets',
    order: 66,
    labels: { de: 'Tickets', en: 'Tickets', vi: 'Vé' },
  },
  [LAYOUT_COMPONENT_TYPE.contactBlock]: {
    hash: 'visit',
    order: 12,
    labels: { de: 'Kontakt', en: 'Contact', vi: 'Liên hệ' },
  },
  [LAYOUT_COMPONENT_TYPE.footerStandard]: {
    hash: 'contact',
    order: 90,
    labels: { de: 'Kontakt', en: 'Contact', vi: 'Liên hệ' },
  },
};

export interface SiteSectionOption {
  /** Stored CTA href (`/#reserve`, `/about-us#party`). */
  href: string;
  hash: string;
  label: string;
  order: number;
  componentType: string;
}

/** Place name used only when two sections would otherwise share a label. */
const FOOTER_PLACE_LABELS = {
  de: 'Fußzeile',
  en: 'Footer',
  vi: 'Chân trang',
} as const;

const HOME_PLACE_LABELS = {
  de: 'Startseite',
  en: 'Home',
  vi: 'Trang chủ',
} as const;

export interface ListSiteSectionOptionsInput {
  globalBlocks?: LayoutBlock[];
  /** pageId → blocks */
  pageBlocks?: Record<string, LayoutBlock[]>;
  /** Flat list when page scoping is already resolved. */
  allBlocks?: LayoutBlock[];
  pages?: PageMeta[];
  locale?: string;
}

function pickLabel(
  labels: SiteSectionHashMeta['labels'],
  locale: string,
): string {
  if (locale === 'en') return labels.en;
  if (locale === 'vi') return labels.vi;
  return labels.de;
}

function homepagePageId(pages: PageMeta[] | undefined): string | null {
  if (!pages?.length) {
    return null;
  }
  const home = pages.find((page) => page.slug === PAGE_SLUG.index);
  return home?.pageId ?? null;
}

function pageSlugForBlock(
  block: LayoutBlock,
  pages: PageMeta[] | undefined,
  homePageId: string | null,
): string {
  if (block.pageId == null || block.pageId === homePageId) {
    return PAGE_SLUG.index;
  }
  const page = pages?.find((entry) => entry.pageId === block.pageId);
  return page?.slug ?? PAGE_SLUG.index;
}

function hrefForSection(slug: string, hash: string): string {
  const path = publicPathForPageSlug(slug);
  if (path === '/') {
    return `/#${hash}`;
  }
  return `${path}#${hash}`;
}

function pickPageTitle(page: PageMeta, locale: string): string {
  const titles = page.localizedTitle;
  const direct = titles[locale]?.trim();
  if (direct) {
    return direct;
  }
  const fallback = titles.de?.trim() || Object.values(titles).find((value) => value.trim());
  return fallback?.trim() ?? '';
}

interface SectionDraft extends SiteSectionOption {
  pageSlug: string;
}

function placeLabel(draft: SectionDraft, pages: PageMeta[] | undefined, locale: string): string {
  if (draft.componentType === LAYOUT_COMPONENT_TYPE.footerStandard) {
    return pickLabel(FOOTER_PLACE_LABELS, locale);
  }
  const page = pages?.find((entry) => entry.slug === draft.pageSlug);
  const title = page ? pickPageTitle(page, locale) : '';
  if (title) {
    return title;
  }
  if (draft.pageSlug === PAGE_SLUG.index) {
    return pickLabel(HOME_PLACE_LABELS, locale);
  }
  return draft.pageSlug;
}

function toSectionOption(draft: SectionDraft): SiteSectionOption {
  return {
    href: draft.href,
    hash: draft.hash,
    label: draft.label,
    order: draft.order,
    componentType: draft.componentType,
  };
}

function sameLabel(a: string, b: string, locale: string): boolean {
  return a.localeCompare(b, locale, { sensitivity: 'accent' }) === 0;
}

/** When two targets share a name, append the page or footer so the owner can tell them apart. */
function disambiguateSectionLabels(
  drafts: SectionDraft[],
  pages: PageMeta[] | undefined,
  locale: string,
): SiteSectionOption[] {
  const counts = new Map<string, number>();
  for (const draft of drafts) {
    counts.set(draft.label, (counts.get(draft.label) ?? 0) + 1);
  }

  const labeled = drafts.map((draft) => {
    const option = toSectionOption(draft);
    if ((counts.get(draft.label) ?? 0) < 2) {
      return option;
    }
    const place = placeLabel(draft, pages, locale);
    if (!place || sameLabel(place, draft.label, locale)) {
      return option;
    }
    return { ...option, label: `${draft.label} · ${place}` };
  });

  const finalCounts = new Map<string, number>();
  for (const option of labeled) {
    finalCounts.set(option.label, (finalCounts.get(option.label) ?? 0) + 1);
  }
  return labeled.map((option) => {
    if ((finalCounts.get(option.label) ?? 0) < 2) {
      return option;
    }
    return { ...option, label: `${option.label} · ${option.href}` };
  });
}

function flattenBlocks(input: ListSiteSectionOptionsInput): LayoutBlock[] {
  if (input.allBlocks && input.allBlocks.length > 0) {
    return input.allBlocks;
  }
  const out: LayoutBlock[] = [...(input.globalBlocks ?? [])];
  for (const blocks of Object.values(input.pageBlocks ?? {})) {
    out.push(...blocks);
  }
  return out;
}

/**
 * Unique section destinations currently present on the site (by href).
 * Footer `#contact` is included when a footer block exists.
 */
export function listSiteSectionOptions(
  input: ListSiteSectionOptionsInput,
): SiteSectionOption[] {
  const locale = input.locale ?? 'de';
  const homePageId = homepagePageId(input.pages);
  const byHref = new Map<string, SectionDraft>();

  for (const block of flattenBlocks(input)) {
    const meta = SITE_SECTION_BY_COMPONENT_TYPE[block.componentType];
    if (!meta) {
      continue;
    }
    const slug = pageSlugForBlock(block, input.pages, homePageId);
    const href = hrefForSection(slug, meta.hash);
    const existing = byHref.get(href);
    if (existing && existing.order <= meta.order) {
      continue;
    }
    byHref.set(href, {
      href,
      hash: meta.hash,
      label: pickLabel(meta.labels, locale),
      order: meta.order,
      componentType: block.componentType,
      pageSlug: slug,
    });
  }

  const sections = disambiguateSectionLabels([...byHref.values()], input.pages, locale);
  return sections.sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, locale));
}

/** True when href looks like an in-page / on-site section destination. */
export function isSiteSectionHref(href: string): boolean {
  const h = href.trim();
  if (!h) {
    return true;
  }
  if (h.startsWith('tel:') || /^https?:\/\//i.test(h)) {
    return false;
  }
  return h.includes('#') || (h.startsWith('/') && !/^https?:/i.test(h));
}

/** Prefer a matching live section; otherwise first option; otherwise `/#contact`. */
export function pickDefaultSectionHref(
  sections: readonly SiteSectionOption[],
  preferredHref?: string | null,
): string {
  const preferred = (preferredHref ?? '').trim();
  if (preferred && sections.some((section) => section.href === preferred)) {
    return preferred;
  }
  if (preferred && isSiteSectionHref(preferred) && !preferred.startsWith('tel:')) {
    const hash = preferred.includes('#') ? preferred.slice(preferred.indexOf('#')) : '';
    const byHash = hash
      ? sections.find((section) => `#${section.hash}` === hash || section.href.endsWith(hash))
      : undefined;
    if (byHash) {
      return byHash.href;
    }
  }
  if (sections.length > 0) {
    return sections[0]!.href;
  }
  return '/#contact';
}
